const DISMISSED_AT_KEY = "notif-prompt-dismissed-at";
const DISMISS_COUNT_KEY = "notif-prompt-dismiss-count";
const SESSION_SHOWN_KEY = "notif-prompt-shown-this-session";
const ENGAGEMENT_KEY = "notif-prompt-engagement-count";

const DAY_MS = 24 * 60 * 60 * 1000;
const DISMISS_COOLDOWN_DAYS = [3, 14];
const MAX_DISMISSALS = 3;
const MIN_ENGAGEMENT = 2;

function safeLocal(): Storage | null {
	if (typeof window === "undefined") return null;
	try {
		return window.localStorage;
	} catch {
		return null;
	}
}

function safeSession(): Storage | null {
	if (typeof window === "undefined") return null;
	try {
		return window.sessionStorage;
	} catch {
		return null;
	}
}

export function getDismissCount(): number {
	const raw = safeLocal()?.getItem(DISMISS_COUNT_KEY);
	const n = raw ? Number.parseInt(raw, 10) : 0;
	return Number.isFinite(n) ? n : 0;
}

function getDismissedAt(): number | null {
	const raw = safeLocal()?.getItem(DISMISSED_AT_KEY);
	if (!raw) return null;
	const n = Number.parseInt(raw, 10);
	return Number.isFinite(n) ? n : null;
}

export function recordDismiss(): void {
	const store = safeLocal();
	if (!store) return;
	const count = getDismissCount() + 1;
	store.setItem(DISMISS_COUNT_KEY, String(count));
	store.setItem(DISMISSED_AT_KEY, String(Date.now()));
}

export function recordPermanentDismiss(): void {
	const store = safeLocal();
	if (!store) return;
	store.setItem(DISMISS_COUNT_KEY, String(MAX_DISMISSALS));
	store.setItem(DISMISSED_AT_KEY, String(Date.now()));
}

export function isInCooldown(): boolean {
	const count = getDismissCount();
	if (count >= MAX_DISMISSALS) return true;
	const dismissedAt = getDismissedAt();
	if (!dismissedAt) return false;
	const cooldownDays =
		DISMISS_COOLDOWN_DAYS[
			Math.min(count - 1, DISMISS_COOLDOWN_DAYS.length - 1)
		];
	if (!cooldownDays) return false;
	return Date.now() - dismissedAt < cooldownDays * DAY_MS;
}

export function markShownThisSession(): void {
	safeSession()?.setItem(SESSION_SHOWN_KEY, "1");
}

export function wasShownThisSession(): boolean {
	return safeSession()?.getItem(SESSION_SHOWN_KEY) === "1";
}

export function bumpEngagement(): number {
	const store = safeSession();
	if (!store) return 0;
	const raw = store.getItem(ENGAGEMENT_KEY);
	const next = (raw ? Number.parseInt(raw, 10) || 0 : 0) + 1;
	store.setItem(ENGAGEMENT_KEY, String(next));
	return next;
}

export function getEngagement(): number {
	const raw = safeSession()?.getItem(ENGAGEMENT_KEY);
	const n = raw ? Number.parseInt(raw, 10) : 0;
	return Number.isFinite(n) ? n : 0;
}

export function hasMinEngagement(): boolean {
	return getEngagement() >= MIN_ENGAGEMENT;
}

export function resetNotifPromptState(): void {
	const store = safeLocal();
	if (!store) return;
	store.removeItem(DISMISSED_AT_KEY);
	store.removeItem(DISMISS_COUNT_KEY);
}
